<script setup lang="ts">
import { nextTick, ref, useTemplateRef } from "vue";
import { usePreferredReducedMotion } from "@vueuse/core";

// Easter egg: hovering the BestConsultant pictogram plays one of these
// animations, drawn at random according to its weight (the lower, the rarer).
type LogoAnimation = "spin" | "heartbeat" | "bloom" | "flip" | "drop" | "burst" | "rainbow";

const ANIMATIONS: { name: LogoAnimation; weight: number }[] = [
  { name: "spin", weight: 30 }, // common
  { name: "heartbeat", weight: 25 }, // common
  { name: "bloom", weight: 18 }, // uncommon
  { name: "flip", weight: 12 }, // rare
  { name: "drop", weight: 8 }, // very rare
  { name: "burst", weight: 5 }, // epic
  { name: "rainbow", weight: 2 }, // legendary
];
const TOTAL_WEIGHT = ANIMATIONS.reduce((total, animation) => total + animation.weight, 0);

// The six petals, clockwise from the top-right one. `x`/`y` is the direction
// the petal points to from the logo center, used to push it outwards.
const PETALS = [
  {
    x: 0.5,
    y: -0.866,
    d: "M141.5,87.88c0.39,2.12,2.59,3.39,4.62,2.67l39.2-13.95c18-6.4,28.69-24.93,25.24-43.72l0,0C206.23,9.3,181.81-4.8,159.22,3.24l0,0c-18,6.4-28.69,24.93-25.24,43.72L141.5,87.88z",
  },
  {
    x: 1,
    y: 0,
    d: "M241.8,86.98L241.8,86.98c-14.55-12.39-35.93-12.39-50.48,0l-31.68,26.97c-1.64,1.4-1.64,3.94,0,5.34l31.68,26.97c14.55,12.39,35.93,12.39,50.48,0l0,0C260.05,130.72,260.05,102.53,241.8,86.98z",
  },
  {
    x: 0.5,
    y: 0.866,
    d: "M185.32,156.64l-39.2-13.95c-2.03-0.72-4.23,0.55-4.62,2.67l-7.52,40.92c-3.45,18.79,7.24,37.31,25.24,43.72l0,0c22.59,8.04,47.01-6.06,51.34-29.64l0,0C214.02,181.57,203.32,163.05,185.32,156.64z",
  },
  {
    x: -0.5,
    y: 0.866,
    d: "M114.47,145.37c-0.39-2.12-2.59-3.39-4.62-2.67l-39.2,13.95c-18,6.4-28.69,24.93-25.24,43.72l0,0c4.33,23.58,28.75,37.68,51.34,29.64l0,0c18-6.4,28.69-24.93,25.24-43.72L114.47,145.37z",
  },
  {
    x: -1,
    y: 0,
    d: "M96.34,119.29c1.64-1.4,1.64-3.94,0-5.34L64.66,86.98c-14.55-12.39-35.93-12.39-50.48,0l0,0c-18.26,15.54-18.26,43.74,0,59.28l0,0c14.55,12.39,35.93,12.39,50.48,0L96.34,119.29z",
  },
  {
    x: -0.5,
    y: -0.866,
    d: "M70.65,76.6l39.2,13.95c2.03,0.72,4.23-0.55,4.62-2.67l7.52-40.92c3.45-18.79-7.24-37.31-25.24-43.72l0,0C74.16-4.8,49.75,9.3,45.41,32.88l0,0C41.96,51.68,52.65,70.2,70.65,76.6z",
  },
];

const svg = useTemplateRef<SVGSVGElement>("svg");
const current = ref<LogoAnimation | null>(null);
// Petal falling in the "drop" animation: only the two bottom ones can fall without crossing the others.
const droppedPetal = ref(2);
const reducedMotion = usePreferredReducedMotion();

function pickAnimation(): LogoAnimation {
  let roll = Math.random() * TOTAL_WEIGHT;
  for (const animation of ANIMATIONS) {
    roll -= animation.weight;
    if (roll < 0) return animation.name;
  }
  return ANIMATIONS[0].name;
}

async function play() {
  if (current.value || reducedMotion.value === "reduce") return;
  current.value = pickAnimation();
  droppedPetal.value = Math.random() < 0.5 ? 2 : 3;
  await nextTick();
  // Wait for every CSS animation (logo + staggered petals) before allowing a new one.
  const animations = svg.value?.getAnimations?.({ subtree: true }) ?? [];
  await Promise.allSettled(animations.map((animation) => animation.finished));
  current.value = null;
}
</script>

<template>
  <span class="inline-flex" @pointerenter="play">
    <svg
      ref="svg"
      class="bc-logo max-w-full"
      :class="current && `bc-logo--${current}`"
      viewBox="0 0 256 233"
      width="62"
      height="56"
      role="img"
      aria-label="BestConsultant"
    >
      <path
        v-for="(petal, index) in PETALS"
        :key="index"
        class="bc-logo__petal"
        :class="{ 'bc-logo__petal--dropped': index === droppedPetal }"
        :d="petal.d"
        :style="{ '--i': index, '--x': petal.x, '--y': petal.y, '--r': index % 2 ? -1 : 1 }"
      />
    </svg>
  </span>
</template>

<style scoped>
/* Corporate blue of the pictogram: not the firm-customisable `primary` token. */
.bc-logo {
  overflow: visible;
  fill: #116dff;
}

.bc-logo__petal {
  transform-box: fill-box;
  transform-origin: center;
}

/* Common — the logo has a 6-fold symmetry, so a 120° turn lands on the same picture. */
.bc-logo--spin {
  animation: bc-logo-spin 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes bc-logo-spin {
  to {
    transform: rotate(120deg);
  }
}

/* Common — a double heartbeat. */
.bc-logo--heartbeat {
  animation: bc-logo-heartbeat 1000ms ease-in-out;
}

@keyframes bc-logo-heartbeat {
  14% {
    transform: scale(1.15);
  }
  28% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.12);
  }
  70% {
    transform: scale(1);
  }
}

/* Uncommon — petals open outwards one after the other, like a flower blooming. */
.bc-logo--bloom .bc-logo__petal {
  animation: bc-logo-bloom 800ms cubic-bezier(0.45, 0, 0.55, 1) calc(var(--i) * 50ms);
}

@keyframes bc-logo-bloom {
  45% {
    transform: translate(calc(var(--x) * 24px), calc(var(--y) * 24px)) scale(1.08);
  }
}

/* Rare — tossed like a coin. */
.bc-logo--flip {
  animation: bc-logo-flip 900ms cubic-bezier(0.65, 0, 0.35, 1);
}

@keyframes bc-logo-flip {
  50% {
    transform: perspective(200px) translateY(-8px) rotateY(180deg) scale(1.1);
  }
  to {
    transform: perspective(200px) translateY(0) rotateY(360deg) scale(1);
  }
}

/* Very rare — a bottom petal wobbles on its base, falls off, then grows back in place. */
.bc-logo--drop .bc-logo__petal--dropped {
  transform-origin: calc(50% - var(--x) * 40%) calc(50% - var(--y) * 40%);
  animation: bc-logo-drop 1600ms ease-in-out;
}

@keyframes bc-logo-drop {
  8% {
    transform: translate(0, 0) rotate(calc(var(--r) * -10deg)) scale(1);
  }
  16% {
    transform: translate(0, 0) rotate(calc(var(--r) * 8deg)) scale(1);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.55, 0, 1, 0.45);
  }
  50% {
    transform: translate(calc(var(--r) * 20px), 140px) rotate(calc(var(--r) * 80deg)) scale(0.9);
    opacity: 0;
  }
  51% {
    transform: translate(0, 0) rotate(0deg) scale(0);
    opacity: 0;
    animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  80% {
    transform: translate(0, 0) rotate(0deg) scale(1);
    opacity: 1;
  }
}

/* Epic — petals gather, burst out spinning, then snap back into place. */
.bc-logo--burst .bc-logo__petal {
  animation: bc-logo-burst 1300ms cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes bc-logo-burst {
  12% {
    transform: translate(calc(var(--x) * -12px), calc(var(--y) * -12px)) rotate(0deg) scale(0.9);
  }
  35%,
  55% {
    transform: translate(calc(var(--x) * 70px), calc(var(--y) * 70px))
      rotate(calc(var(--r) * 120deg)) scale(0.75);
  }
  82% {
    transform: translate(calc(var(--x) * -6px), calc(var(--y) * -6px)) rotate(0deg) scale(1.04);
  }
}

/* Legendary — a rainbow wheel sweeps the petals while the logo spins twice with a glow. */
.bc-logo--rainbow {
  animation: bc-logo-rainbow-spin 2550ms cubic-bezier(0.65, 0, 0.35, 1);
}

.bc-logo--rainbow .bc-logo__petal {
  animation: bc-logo-rainbow-hue 900ms linear calc(var(--i) * 150ms) 2;
}

@keyframes bc-logo-rainbow-spin {
  50% {
    transform: rotate(360deg) scale(1.2);
    filter: drop-shadow(0 0 6px rgb(17 109 255 / 0.6));
  }
  to {
    transform: rotate(720deg) scale(1);
  }
}

@keyframes bc-logo-rainbow-hue {
  0%,
  100% {
    fill: #116dff;
  }
  17% {
    fill: hsl(277deg 100% 62%);
  }
  33% {
    fill: hsl(337deg 100% 58%);
  }
  50% {
    fill: hsl(37deg 100% 52%);
  }
  67% {
    fill: hsl(97deg 75% 45%);
  }
  83% {
    fill: hsl(167deg 90% 40%);
  }
}
</style>
